import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Send, Eye, Loader2, CheckCircle2, XCircle, MinusCircle } from "lucide-react";
import api from "@/lib/api";
import SegmentFilterBuilder from "./SegmentFilterBuilder";

const STATUS_BADGE = {
  draft: "bg-slate-500/10 text-slate-300",
  queued: "bg-indigo-400/10 text-indigo-300",
  sending: "bg-amber-400/10 text-amber-300",
  completed: "bg-emerald-400/10 text-emerald-300",
  failed: "bg-red-400/10 text-red-300",
};

export default function CampaignsPanel({ twilioConfigured, onCampaignsChanged }) {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [locations, setLocations] = useState({ countries: [], cities: [] });
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [channel, setChannel] = useState("email");
  const [templateId, setTemplateId] = useState("");
  const [filter, setFilter] = useState({ mode: "all", roles: [], tags: [] });
  const [previewCount, setPreviewCount] = useState(null);
  const [creating, setCreating] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [logs, setLogs] = useState({});

  const loadCampaigns = () => {
    api.get("/admin/crm/campaigns").then((r) => setCampaigns(r.data)).catch(() => toast.error("Could not load campaigns"));
  };

  useEffect(() => {
    loadCampaigns();
    api.get("/admin/crm/templates").then((r) => setTemplates(r.data));
    api.get("/admin/crm/contacts/tags").then((r) => setAllTags(r.data));
    api.get("/admin/crm/contacts/locations").then((r) => setLocations(r.data));
    const interval = setInterval(loadCampaigns, 5000);
    return () => clearInterval(interval);
  }, []);

  const templatesForChannel = templates.filter((t) => t.channel === channel);

  const previewSegment = async () => {
    const { data } = await api.post("/admin/crm/segments/preview", filter);
    setPreviewCount(data.count);
  };

  const createCampaign = async () => {
    if (!name.trim() || !templateId) { toast.error("Name and template are required"); return; }
    setCreating(true);
    try {
      await api.post("/admin/crm/campaigns", { name, channel, template_id: templateId, segment: filter });
      toast.success("Campaign created as draft");
      setName(""); setTemplateId(""); setFilter({ mode: "all", roles: [], tags: [] }); setPreviewCount(null);
      setShowForm(false);
      loadCampaigns();
      onCampaignsChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create campaign");
    } finally {
      setCreating(false);
    }
  };

  const sendCampaign = async (id) => {
    setSendingId(id);
    try {
      await api.post(`/admin/crm/campaigns/${id}/send`);
      toast.success("Campaign queued for sending");
      loadCampaigns();
      onCampaignsChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send campaign");
    } finally {
      setSendingId(null);
    }
  };

  const toggleLogs = async (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    if (!logs[id]) {
      const { data } = await api.get(`/admin/crm/campaigns/${id}/logs`);
      setLogs((prev) => ({ ...prev, [id]: data.logs }));
    }
  };

  return (
    <div data-testid="crm-campaigns-panel">
      <div className="flex justify-end mb-4">
        <button onClick={() => setShowForm((s) => !s)} data-testid="crm-add-campaign-toggle-btn"
          className="flex items-center gap-1.5 text-xs font-semibold bg-primary hover:bg-primary/90 text-white px-3 py-2 rounded-lg">
          <Plus className="w-3.5 h-3.5" /> New Campaign
        </button>
      </div>

      {showForm && (
        <div className="glass-panel rounded-xl p-4 mb-6" data-testid="crm-campaign-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Campaign Name"
              data-testid="crm-campaign-name-input" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white" />
            <select value={channel} onChange={(e) => { setChannel(e.target.value); setTemplateId(""); }} data-testid="crm-campaign-channel-select"
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white">
              <option value="email">Email</option>
              <option value="sms" disabled={!twilioConfigured}>SMS {!twilioConfigured && "(coming soon)"}</option>
              <option value="whatsapp" disabled={!twilioConfigured}>WhatsApp {!twilioConfigured && "(coming soon)"}</option>
            </select>
          </div>
          <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} data-testid="crm-campaign-template-select"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white mb-3">
            <option value="">Select a {channel} template…</option>
            {templatesForChannel.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>

          <p className="text-xs font-semibold text-slate-300 mb-2">Audience</p>
          <SegmentFilterBuilder filter={filter} setFilter={setFilter} allTags={allTags} allCountries={locations.countries} allCities={locations.cities} />

          <div className="flex items-center gap-3 mt-3">
            <button onClick={previewSegment} data-testid="crm-preview-segment-btn"
              className="flex items-center gap-1.5 text-xs font-semibold bg-white/5 hover:bg-white/10 text-white px-3 py-2 rounded-lg">
              <Eye className="w-3.5 h-3.5" /> Preview Reach
            </button>
            {previewCount !== null && <span className="text-xs text-slate-300" data-testid="crm-preview-count">{previewCount} contact(s) will receive this campaign</span>}
          </div>

          <button onClick={createCampaign} disabled={creating} data-testid="crm-save-campaign-btn"
            className="mt-4 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-xs font-semibold px-4 py-2 rounded-lg disabled:opacity-50">
            {creating ? "Creating…" : "Create Draft Campaign"}
          </button>
        </div>
      )}

      <div className="space-y-3">
        {campaigns.map((c) => (
          <div key={c.id} className="glass-panel rounded-xl p-4" data-testid={`crm-campaign-card-${c.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-white">{c.name}</p>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_BADGE[c.status] || STATUS_BADGE.draft}`}>{c.status}</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  {c.channel} · {c.stats.total} total · <span className="text-emerald-400">{c.stats.sent} sent</span> · <span className="text-red-400">{c.stats.failed} failed</span> · <span className="text-slate-400">{c.stats.skipped} skipped</span>
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => toggleLogs(c.id)} data-testid={`crm-view-logs-btn-${c.id}`} className="text-xs font-semibold text-slate-300 hover:text-white px-2 py-1.5">
                  {expandedId === c.id ? "Hide Logs" : "View Logs"}
                </button>
                {(c.status === "draft" || c.status === "failed") && (
                  <button onClick={() => sendCampaign(c.id)} disabled={sendingId === c.id} data-testid={`crm-send-campaign-btn-${c.id}`}
                    className="flex items-center gap-1.5 text-xs font-semibold bg-primary hover:bg-primary/90 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg">
                    {sendingId === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send Now
                  </button>
                )}
              </div>
            </div>
            {expandedId === c.id && (
              <div className="mt-3 border-t border-white/5 pt-3 space-y-1.5 max-h-64 overflow-y-auto" data-testid={`crm-logs-list-${c.id}`}>
                {(logs[c.id] || []).length === 0 && <p className="text-xs text-slate-500">No delivery logs yet.</p>}
                {(logs[c.id] || []).map((l) => (
                  <div key={l.id} className="flex items-center gap-2 text-xs" data-testid={`crm-log-row-${l.id}`}>
                    {l.status === "sent" ? <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0" /> : l.status === "failed" ? <XCircle className="w-3 h-3 text-red-400 shrink-0" /> : <MinusCircle className="w-3 h-3 text-slate-500 shrink-0" />}
                    <span className="text-slate-300">{l.contact_email}</span>
                    <span className="text-slate-500">— {l.status}{l.error ? `: ${l.error}` : ""}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {campaigns.length === 0 && <p className="text-sm text-slate-400 text-center py-8" data-testid="crm-no-campaigns">No campaigns yet — create one to reach your contacts.</p>}
      </div>
    </div>
  );
}
